import mixpanel from 'mixpanel-browser';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { isDatadogSyntheticsBrowser } from '@/config';
import { env } from '@/env';
import { isE2E } from '@/utils/e2e';

const EVENT_NAMES = {
  PAGE_VIEWED: 'Page Viewed',
};

const ready = ref(false);

function initMixpanel() {
  const mixpanelConfig = { track_pageview: false, secure_cookie: true };

  if (env.mixpanelApiHost) {
    mixpanelConfig.api_host = env.mixpanelApiHost;
  }

  const enabled = env.mixpanelEnabled && !isDatadogSyntheticsBrowser && !isE2E();
  if (enabled) {
    mixpanel.init(env.mixpanelToken, mixpanelConfig);
    ready.value = true;
  } else {
    mixpanel.init(null, { ...mixpanelConfig, test: true }, 'mixpanel');
    mixpanel.disable();
  }
}
initMixpanel();

function track(eventName, ...args) {
  mixpanel.track(eventName, ...args);
}

/**
 * Track an event immediately, bypassing the batch queue.
 * Returns a promise that resolves once the request completes.
 * Use this when a navigation (e.g. window.location.assign) follows
 * the track call and you need delivery confirmed first.
 */
function trackImmediately(eventName, properties) {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    // Safety net: never block the caller longer than 2s if the
    // Mixpanel callback doesn't fire (ad blocker, offline,
    // mixpanel.disable() no-op path, etc.).
    setTimeout(done, 2000);
    mixpanel.track(eventName, properties, { send_immediately: true }, done);
  });
}

/**
 * Track an event and wait for Mixpanel to acknowledge delivery.
 * Resolves on a successful response, or if the callback never fires
 * within the timeout (mixpanel.disable() in dev, ad blocker, offline) —
 * none of which are user-actionable. Rejects only when Mixpanel reports
 * an explicit delivery failure (e.g. /track returns an HTTP error).
 *
 * Use when the user is invested in whether the event was logged
 * (e.g. AI summary feedback). For fire-and-forget analytics, use track().
 */
function trackAndAck(eventName, properties) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };
    setTimeout(() => settle(resolve), 5000);
    mixpanel.track(eventName, properties, { send_immediately: true }, (response) => {
      // mixpanel-browser callback: response is 1 on success, 0 on failure.
      if (response === 0) {
        settle(reject, new Error('Mixpanel track delivery failed'));
      } else {
        settle(resolve);
      }
    });
  });
}

function registerSuperProperties(properties) {
  if (properties) {
    mixpanel.register(properties);
  }
}

function setIdentity(properties = {}) {
  if (properties) {
    mixpanel.identify(properties.userId);
    mixpanel.people.set(properties);
  }
}

function reset() {
  mixpanel.reset();
}

export const useMixpanelStore = defineStore('mixpanel', () => {
  function trackPage({ page, payload, unload }) {
    if (unload) {
      mixpanel.set_config({ api_transport: 'sendBeacon' });
    }
    registerSuperProperties({ page_sp_test: page, pagetype: page });
    track(EVENT_NAMES.PAGE_VIEWED, payload);
    mixpanel.time_event(EVENT_NAMES.PAGE_VIEWED);
  }

  function setUtmQueryParams({ firstTouchParams = {}, lastTouchParams = {} } = {}) {
    if (Object.keys(firstTouchParams).length > 0) {
      mixpanel.people.set_once(firstTouchParams);
    }
    mixpanel.people.set({
      ...lastTouchParams,
    });
    registerSuperProperties({
      ...lastTouchParams,
    });
  }

  return {
    ready,
    track,
    trackImmediately,
    trackAndAck,
    trackPage,
    registerSuperProperties,
    reset,
    setUtmQueryParams,
    setIdentity,
  };
});
